import { Backdrop, CircularProgress, styled, useTheme } from '@mui/material';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface LoaderOverlayProps {
    open: boolean;
}

const CircularProgressStyled = styled(CircularProgress)(({ theme }) => ({
    color: theme.palette.primary.light,
}));

/**
 * The full-screen loading indicator — rendered into `document.body` (#2807).
 *
 * It used to render where it sat in the tree. A `position: fixed` element
 * resolves against the nearest ancestor that establishes a containing block
 * (a transform, a filter, `will-change`, …), and inside the booking dialogs
 * QA measured it at `top: 820` in an 820 px viewport: one full screen below
 * the fold, so the customer saw no indicator at all — a finished, empty
 * screen. The ancestor responsible was never identified; portalling to the
 * body sidesteps the question for all ~80 call sites instead of betting on a
 * diagnosis.
 *
 * Two consequences of leaving the dialog's subtree, both handled here:
 *
 * - **Stacking.** `zIndex: 2` was enough INSIDE a dialog. From the body it has
 *   to clear the dialogs themselves — Radix `z-50`, MUI modals at
 *   `theme.zIndex.modal` — so it sits one above the MUI modal layer.
 *   Notifications (`theme.zIndex.snackbar`) stay above it, so an error raised
 *   while loading is still readable.
 * - **Blocking.** An open Radix modal sets `pointer-events: none` on the body,
 *   which this element would inherit — and a loader that lets clicks through
 *   stops preventing the double-submit it has always prevented. So it sets
 *   `pointer-events: auto` itself. The booking flows' `FullScreenDialog`
 *   ignores outside interaction, so this cannot close it.
 * - **Screen readers.** An open Radix modal marks every other child of <body>
 *   `aria-hidden` when it mounts (`hideOthers`), which would now include this
 *   element. That call skips `[aria-live]` nodes, so the overlay is a polite
 *   live region with its own label — and "loading" is announced, where before
 *   the spinner had no text at all.
 */
const LoaderOverlay = (props: LoaderOverlayProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const overlay = (
        <Backdrop
            data-testid="loader-overlay"
            role="status"
            aria-live="polite"
            aria-label={t('common.loading')}
            open={props.open}
            style={{ zIndex: theme.zIndex.modal + 1, pointerEvents: 'auto' }}
        >
            <CircularProgressStyled />
        </Backdrop>
    );

    return typeof document === 'undefined'
        ? overlay
        : createPortal(overlay, document.body);
};

export default LoaderOverlay;
